import { useState } from 'react';
import { ArrowRight, CheckCircle, WarningCircle, X, ShieldWarning, Sparkle } from '@phosphor-icons/react';

/**
 * "Is MigrationCrunch right for me?" wizard.
 *
 * Five short questions → honest verdict + tier recommendation.
 * Designed to PROTECT refund rate by steering complex enterprise Salesforce/
 * Dynamics migrations away from the cheap Starter Pro tier (where they'd
 * refund) and toward the Enterprise AI tier OR an honest "consider a human
 * consultant" message.
 *
 * Scoring philosophy:
 *   0-1 points  → ✅ Perfect fit. Cheapest tier wins.
 *   2-4 points  → ⚠️ Good fit with caveats. Match to higher tier.
 *   5+ points   → ❌ Honest steer away from DIY tiers.
 */

const QUESTIONS = [
  {
    id: 'source',
    label: 'Which CRM are you migrating FROM?',
    help: 'Some CRMs have simpler data structures than others. Pick the closest match.',
    options: [
      { value: 'simple', label: 'ACT!, Pipedrive, Less Annoying, Capsule, Insightly, Copper, Freshsales, Keap, monday.com', points: 0 },
      { value: 'medium', label: 'HubSpot (Starter/Pro), Zoho CRM', points: 1 },
      { value: 'complex', label: 'Salesforce, HubSpot Enterprise, Microsoft Dynamics, Sugar, NetSuite', points: 2 },
      { value: 'unknown', label: "I'm not sure / something else", points: 1 },
    ],
  },
  {
    id: 'contacts',
    label: 'About how many contacts do you have?',
    help: "Rough number is fine. Don't count contacts you'd delete anyway.",
    options: [
      { value: 'tiny', label: 'Under 1,000', points: 0 },
      { value: 'small', label: '1,000 – 12,500', points: 0 },
      { value: 'medium', label: '12,500 – 100,000', points: 0 },
      { value: 'large', label: '100,000 – 500,000', points: 1 },
      { value: 'huge', label: 'More than 500,000', points: 2 },
    ],
  },
  {
    id: 'custom_objects',
    label: 'Do you use custom objects or many custom fields?',
    help: 'Custom objects = extra record types beyond contacts/companies/deals (common in Salesforce). 50+ custom fields per contact also counts as "yes."',
    options: [
      { value: 'no', label: "No — just contacts, companies, and maybe deals", points: 0 },
      { value: 'few', label: 'A few custom fields, no custom objects', points: 0 },
      { value: 'many', label: 'Lots of custom fields (50+) OR a couple custom objects', points: 1 },
      { value: 'heavy', label: 'Heavily customized — custom objects everywhere', points: 3 },
      { value: 'unknown', label: "I'm not sure", points: 1 },
    ],
  },
  {
    id: 'workflows',
    label: 'Do you need to preserve workflow automations and reports?',
    help: 'Workflows = "when X happens, do Y automatically." Reports = dashboards with charts. These are CRM-specific code, not data — MigrationCrunch only moves data.',
    options: [
      { value: 'no', label: "No — I'll rebuild those manually in the new CRM", points: 0 },
      { value: 'some', label: 'A few simple automations, I can rebuild them', points: 1 },
      { value: 'many', label: 'Yes — many automations and reports, critical to my business', points: 3 },
    ],
  },
  {
    id: 'priority',
    label: "What's the MOST important thing to preserve?",
    help: 'This tells us whether our patent-pending feature is what you actually need.',
    options: [
      { value: 'notes', label: 'Notes & history (every conversation, every detail)', points: 0, highlight: true },
      { value: 'contacts', label: 'Just the contact list — names, phones, emails', points: 0 },
      { value: 'activities', label: 'Activities, tasks, and calendar history', points: 0 },
      { value: 'all_data', label: 'Everything: contacts, notes, activities, deals, attachments', points: 0 },
      { value: 'automations', label: 'My workflow automations and reports', points: 3 },
    ],
  },
];

function computeVerdict(answers) {
  const total = QUESTIONS.reduce((sum, q) => {
    const choice = q.options.find(o => o.value === answers[q.id]);
    return sum + (choice?.points || 0);
  }, 0);

  // Cross-question signals that trump the raw score
  const isComplexSource = answers.source === 'complex';
  const isHuge = answers.contacts === 'huge';
  const isLarge = answers.contacts === 'large';
  const wantsAutomations = answers.workflows === 'many' || answers.priority === 'automations';
  const heavyCustom = answers.custom_objects === 'heavy';

  // Recommended tier based on contact count alone
  let tierId, tierName, tierPrice;
  if (answers.contacts === 'tiny') {
    tierId = 'free';
    tierName = 'Free Trial';
    tierPrice = '$0';
  } else if (answers.contacts === 'small') {
    tierId = 'starter_pro';
    tierName = 'Starter Pro';
    tierPrice = '$599';
  } else if (answers.contacts === 'medium') {
    tierId = 'growth_ai';
    tierName = 'Growth AI';
    tierPrice = '$1,799';
  } else if (answers.contacts === 'large') {
    tierId = 'enterprise_ai';
    tierName = 'Enterprise AI';
    tierPrice = '$4,499';
  } else {
    tierId = 'unlimited_ai';
    tierName = 'Unlimited AI';
    tierPrice = '$7,999';
  }

  // ❌ Honest steer away
  if (wantsAutomations || (heavyCustom && (isComplexSource || isLarge || isHuge))) {
    return {
      level: 'not_a_fit',
      headline: "Be honest with yourself — MigrationCrunch alone isn't enough here.",
      body: 'You need to preserve workflow automations, reports, or heavily customized objects. MigrationCrunch moves your DATA brilliantly (contacts, notes, activities), but it does NOT rebuild your automations or recreate your reports in the new CRM. Those need to be redone manually OR you need a human consultant for the full project.',
      recommendation: 'enterprise_or_consultant',
      tierId: 'enterprise_ai',
      tierName: 'Enterprise AI',
      tierPrice: '$4,499',
      details: [
        '✅ We CAN move your contacts, notes, history, activities, and deals — perfectly.',
        '❌ We CANNOT recreate your workflow automations (Process Builder, Flows, Workflows).',
        '❌ We CANNOT rebuild your reports, dashboards, or custom views.',
        '💡 Enterprise AI tier includes a dedicated migration engineer who can guide you through the gaps.',
        '💡 OR hire a CRM consultant for the full project (we partner with several — ask us).',
      ],
    };
  }

  // ⚠️ Good fit with caveats
  if (total >= 2 || isComplexSource || heavyCustom) {
    return {
      level: 'caveats',
      headline: "Good fit — but here's what we'll handle and what you'll need to do yourself.",
      body: `Your migration is feasible with MigrationCrunch, but it's not "just upload a CSV." Your source CRM has some complexity. Recommended tier: ${tierName}.`,
      recommendation: 'caveat_tier',
      tierId,
      tierName,
      tierPrice,
      details: [
        '✅ Contacts, companies, notes, history, activities — all migrated cleanly.',
        '✅ Patent-pending AI re-links notes to the right contacts (our core moat).',
        '⚠️ Custom fields may need manual review — we map what we can, you confirm the rest.',
        ...(isComplexSource ? ['⚠️ Salesforce-specific concepts (Person Accounts, Lead/Contact split) will be flattened to a simple contact model. Some structure is lost.'] : []),
        ...(heavyCustom ? ['⚠️ Custom objects will be flattened into notes/text fields. Not lost — but not "native" in the new CRM.'] : []),
        '💡 Budget ~1 day post-migration for spot-check + cleanup vs. weeks for a manual job.',
      ],
    };
  }

  // ✅ Perfect fit
  return {
    level: 'perfect_fit',
    headline: "You're our ideal customer. This is going to be easy.",
    body: `Your migration is exactly what we built MigrationCrunch for. Recommended tier: ${tierName}.`,
    recommendation: 'perfect_tier',
    tierId,
    tierName,
    tierPrice,
    details: [
      '✅ Upload your CSV → AI maps every field automatically.',
      '✅ Patent-pending AI re-links every note to the right contact — competitors leave this broken.',
      '✅ Pre-flight check shows you exactly what will move BEFORE you commit.',
      '✅ Realistic time: minutes (not the "months" big consultants quote).',
      '💡 30-day money-back guarantee on AI note recovery if anything looks off.',
    ],
  };
}

export default function MigrationFitWizard({ onSelectTier }) {
  const [step, setStep] = useState(-1); // -1 = pre-start, 0..4 = question, 5 = verdict
  const [answers, setAnswers] = useState({});

  const start = () => setStep(0);
  const reset = () => { setStep(-1); setAnswers({}); };

  const handleAnswer = (qid, value) => {
    const next = { ...answers, [qid]: value };
    setAnswers(next);
    if (step + 1 < QUESTIONS.length) {
      setStep(step + 1);
    } else {
      setStep(QUESTIONS.length); // verdict screen
    }
  };

  if (step === -1) {
    return (
      <section id="fit-check" className="border-t border-white/10 px-6 py-12 bg-gradient-to-b from-amber-500/5 to-transparent" data-testid="mc-fit-wizard">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest text-amber-300 uppercase mb-2">
            <ShieldWarning size={12} weight="fill" /> Honest check
          </div>
          <h2 className="text-2xl md:text-3xl font-black mb-3">
            Is MigrationCrunch right for me?
          </h2>
          <p className="text-white/70 mb-6 text-sm md:text-base">
            5 yes/no questions. 30 seconds. We&apos;ll tell you honestly if we&apos;re a great fit,
            a partial fit, or if you need a human consultant instead. No upsells, no BS.
          </p>
          <button
            onClick={start}
            className="px-6 py-3 bg-amber-400 hover:bg-amber-300 text-black font-bold rounded-md transition-colors inline-flex items-center gap-2"
            data-testid="mc-fit-wizard-start-btn"
          >
            Take the 30-second check <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </section>
    );
  }

  // Verdict screen
  if (step >= QUESTIONS.length) {
    const verdict = computeVerdict(answers);
    const iconMap = {
      perfect_fit: <CheckCircle size={32} weight="fill" className="text-emerald-400" />,
      caveats: <WarningCircle size={32} weight="fill" className="text-amber-400" />,
      not_a_fit: <X size={32} weight="bold" className="text-rose-400" />,
    };
    const bgMap = {
      perfect_fit: 'from-emerald-500/15 to-emerald-500/5 border-emerald-500/40',
      caveats: 'from-amber-500/15 to-amber-500/5 border-amber-500/40',
      not_a_fit: 'from-rose-500/15 to-rose-500/5 border-rose-500/40',
    };
    return (
      <section className="border-t border-white/10 px-6 py-12 bg-gradient-to-b from-amber-500/5 to-transparent" data-testid="mc-fit-wizard-verdict">
        <div className="max-w-2xl mx-auto">
          <div className={`bg-gradient-to-b ${bgMap[verdict.level]} border rounded-lg p-6 md:p-8`}>
            <div className="flex items-start gap-3 mb-4">
              {iconMap[verdict.level]}
              <h2 className="text-xl md:text-2xl font-black leading-snug" data-testid="mc-fit-verdict-headline">
                {verdict.headline}
              </h2>
            </div>
            <p className="text-white/80 mb-5 text-sm md:text-base" data-testid="mc-fit-verdict-body">
              {verdict.body}
            </p>
            <ul className="space-y-2 mb-6">
              {verdict.details.map((d, i) => (
                <li key={i} className="text-sm text-white/75 leading-relaxed">{d}</li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/10">
              {verdict.level !== 'not_a_fit' ? (
                <button
                  onClick={() => onSelectTier && onSelectTier(verdict.tierId)}
                  className="flex-1 px-4 py-3 bg-amber-400 hover:bg-amber-300 text-black font-bold rounded-md transition-colors flex items-center justify-center gap-2"
                  data-testid="mc-fit-verdict-cta-tier"
                >
                  <Sparkle size={14} weight="fill" />
                  Get started with {verdict.tierName} ({verdict.tierPrice})
                </button>
              ) : (
                <a
                  href="mailto:chip@migrationcrunch.com?subject=Complex%20migration%20-%20need%20guidance"
                  className="flex-1 px-4 py-3 bg-amber-400 hover:bg-amber-300 text-black font-bold rounded-md transition-colors flex items-center justify-center gap-2"
                  data-testid="mc-fit-verdict-cta-contact"
                >
                  Email us — we&apos;ll help you decide
                </a>
              )}
              <button
                onClick={reset}
                className="px-4 py-3 border border-white/20 hover:border-white/40 text-white/70 hover:text-white rounded-md transition-colors text-sm"
                data-testid="mc-fit-verdict-restart"
              >
                Start over
              </button>
            </div>
          </div>
          <p className="text-center text-[11px] text-white/40 mt-4">
            This check is based on the answers you gave. Edge cases happen — email{' '}
            <a href="mailto:chip@migrationcrunch.com" className="underline hover:text-white/70">
              chip@migrationcrunch.com
            </a>{' '}
            if you want a human to look at your specific setup before buying.
          </p>
        </div>
      </section>
    );
  }

  // Question screen
  const q = QUESTIONS[step];
  return (
    <section className="border-t border-white/10 px-6 py-12 bg-gradient-to-b from-amber-500/5 to-transparent" data-testid={`mc-fit-wizard-q${step}`}>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <span className="text-[10px] font-bold tracking-widest text-amber-300 uppercase">
            Question {step + 1} of {QUESTIONS.length}
          </span>
          <div className="flex items-center gap-3">
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="text-[11px] text-white/60 hover:text-amber-300 underline"
                data-testid="mc-fit-wizard-back"
              >
                ← previous
              </button>
            )}
            <button
              onClick={reset}
              className="text-[11px] text-white/40 hover:text-white/70 underline"
              data-testid="mc-fit-wizard-cancel"
            >
              cancel
            </button>
          </div>
        </div>
        {/* progress bar */}
        <div className="h-1 bg-white/10 rounded-full overflow-hidden mb-6">
          <div
            className="h-full bg-amber-400 transition-all duration-300"
            style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
          />
        </div>
        <h3 className="text-xl md:text-2xl font-black mb-2 leading-snug">
          {q.label}
        </h3>
        <p className="text-white/60 text-sm mb-5">{q.help}</p>
        <div className="space-y-2">
          {q.options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleAnswer(q.id, opt.value)}
              className={`w-full text-left px-4 py-3 rounded-md border transition-colors flex items-center justify-between gap-3 text-sm md:text-base ${
                opt.highlight
                  ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 hover:border-emerald-500/60'
                  : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/30'
              }`}
              data-testid={`mc-fit-q${step}-opt-${opt.value}`}
            >
              <span className="text-white/85">{opt.label}</span>
              <ArrowRight size={14} weight="bold" className="text-white/40 flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
